import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  InputDate,
  InputDecimal,
  InputText,
} from '@/components/_common';
import {
  InvoiceCreateDTO,
  InvoiceGeneralFormValues,
  InvoiceSituationColor,
  OperationDestination,
} from '@/core/domain/invoice/invoice/Invoice.types';
import {
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
  useDefaultMinLengthFieldError,
} from '@/hooks';
import {
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Typography,
} from 'antd';
import { FormInstance } from 'antd/es/form/Form';
import { useIntl } from 'react-intl';
import InvoiceAddressFields from '../InvoiceAddressFields';
import {
  InvoiceDocumentType,
  InvoiceEmissionGoal,
  InvoiceEmissionType,
  InvoiceProvider,
  InvoiceSituation,
  InvoiceType,
} from '@/core/domain/invoice/invoices/invoices.enums';
import { useInvoiceSettingsByCompany } from '@/core/domain/invoice/settings/Settings.hooks';
import { useEffect } from 'react';
import { InvoiceNumberings } from '@/core/domain/invoice/settings/Settings.types';
import StatusTag from '@/components/_common/StatusTag';
import { useInvoiceFormContext } from '../../InvoiceFormProvider';
import EmissionGoalSelect from '@/components/_common/EmissionGoalSelect';
import TaxationNatureSelect from '@/components/_common/NatureOfOperationSelect';
import SelectYesNo from '@/components/_common/SelectYesNo';
import SelectGovernmentType from '@/components/_common/TaxReform/SelectGovernmentType';
import SelectGovernmentOperation from '@/components/_common/TaxReform/SelectGovernmentOperation';
import SelectOperationType from '@/components/_common/TaxReform/SelectOperationType';

interface Props {
  isEdit?: boolean;
  selecteInvoiceType?: InvoiceType;
  form: FormInstance<InvoiceGeneralFormValues>;
  invoiceData?: InvoiceCreateDTO;
}

const InvoiceAllForm = ({
  selecteInvoiceType,
  isEdit,
  form,
  invoiceData,
}: Props) => {
  const intl = useIntl();

  const {
    disabledFields,
    invoice: { data: invoice },
    companyProvider,
    invoiceSettingNfs,
    isInternalApiIssued,
  } = useInvoiceFormContext();

  const hasOriginReference = !!invoice?.originReference?.length;

  const situation = invoice?.situation;

  const companyIdField = Form.useWatch(['companyId'], form);
  const emissionType = Form.useWatch(['emissionType'], form);

  const { data } = useInvoiceSettingsByCompany(Number(companyIdField));

  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();
  const defaultMinLenghtFieldError = useDefaultMinLengthFieldError();

  const handleOptionsSeries = () => {
    if (selecteInvoiceType === InvoiceType.NFE) {
      return data?.configNfe?.invoiceNumberings?.map((n) => ({
        value: n.series,
        label: n.series,
      }));
    }

    if (selecteInvoiceType === InvoiceType.NFC) {
      return data?.configNfc?.invoiceNumberings?.map((n) => ({
        value: n.series,
        label: n.series,
      }));
    }

    if (selecteInvoiceType === InvoiceType.NFS) {
      return data?.configNfs?.invoiceNumberings?.map((n) => ({
        value: n.series,
        label: n.series,
      }));
    }

    return [];
  };

  const handleSeries = (invoiceNumberings: InvoiceNumberings[]) => {
    return invoiceNumberings?.filter((n) => n.main)[0];
  };

  useEffect(() => {
    if (!isEdit) {
      if (selecteInvoiceType === 'NFE' && data?.configNfe?.invoiceNumberings) {
        const serie = handleSeries(data.configNfe.invoiceNumberings)?.series;
        form.setFieldValue('serie', serie);
      }

      if (selecteInvoiceType === 'NFC' && data?.configNfc?.invoiceNumberings) {
        const serie = handleSeries(data.configNfc.invoiceNumberings)?.series;
        form.setFieldValue('serie', serie);
      }

      if (selecteInvoiceType === 'NFS' && data?.configNfs?.invoiceNumberings) {
        const serie = handleSeries(data.configNfs.invoiceNumberings)?.series;
        form.setFieldValue('serie', serie);
      }
    }
  }, [data]);

  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const isAuthorizedOrCanceled =
    situation &&
    (situation === InvoiceSituation.AUTHORIZED ||
      situation === InvoiceSituation.CANCELED);

  const isRelevantInvoiceType =
    invoice?.invoiceType === InvoiceType.NFE ||
    invoice?.invoiceType === InvoiceType.NFC ||
    (invoice?.invoiceType === InvoiceType.NFS &&
      invoiceSettingNfs?.activeNfseNacional);

  const shouldShowKeyField =
    isAuthorizedOrCanceled && isEdit && isRelevantInvoiceType;

  return (
    <>
      <Row gutter={[16, 9]}>
        <Form.Item name="situation" hidden />
        <Form.Item name="invoiceType" hidden />
        <Form.Item name="integrationId" hidden />

        {isEdit && !!situation && (
          <Col span={24}>
            <Typography.Title level={4}>
              Situação:{' '}
              {
                <StatusTag
                  style={{ marginLeft: 5 }}
                  color={InvoiceSituationColor[situation]}
                  label={intl.formatMessage({
                    id: `invoice.situation.${situation}`,
                  })}
                />
              }
            </Typography.Title>
          </Col>
        )}

        <Col span={24} md={12} lg={6}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'invoice.domain.company',
            })}
            name="companyId"
            rules={[defaultEmptyFieldError]}
            style={{ margin: 0, width: '100%', flex: 1 }}
          >
            <CompaniesInfiniteSelectField
              disabled={disabledFields || hasOriginReference}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={6}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'invoice.domain.series',
            })}
            name="serie"
            rules={[defaultEmptyFieldError]}
          >
            <Select options={handleOptionsSeries()} disabled={disabledFields} />
          </FloatingFormItem>
        </Col>

        {situation &&
          situation !== InvoiceSituation.IN_TYPING &&
          situation !== InvoiceSituation.VALIDATED &&
          isEdit && (
            <Col span={24} md={12} lg={6}>
              <FloatingFormItem label="Numeração" name="number">
                <Input disabled />
              </FloatingFormItem>
            </Col>
          )}

        {shouldShowKeyField && (
          <Col span={24} md={12} lg={6}>
            <FloatingFormItem label="Chave" name="key">
              <Input disabled />
            </FloatingFormItem>
          </Col>
        )}

        <Col span={24} md={12} lg={6}>
          <FloatingFormItem
            label={
              selecteInvoiceType === InvoiceType.NFS
                ? 'Natureza da tributação'
                : 'Natureza da operação'
            }
            name={
              selecteInvoiceType === InvoiceType.NFS
                ? 'taxationNature'
                : 'natureOfOperation'
            }
            rules={[defaultEmptyFieldError]}
          >
            {selecteInvoiceType === InvoiceType.NFS ? (
              <TaxationNatureSelect disabled={disabledFields} />
            ) : (
              <Input disabled={disabledFields} />
            )}
          </FloatingFormItem>
        </Col>

        {selecteInvoiceType === InvoiceType.NFS &&
        ((!data?.configNfs?.automaticNumbering &&
          (invoice?.situation === InvoiceSituation.IN_TYPING ||
            invoice?.situation === InvoiceSituation.PROCESSING)) ||
          invoice?.situation === InvoiceSituation.AUTHORIZED ||
          invoice?.situation === InvoiceSituation.CANCELED) ? (
          <Col span={24} md={12} lg={6}>
            <FloatingFormItem
              noStyle
              label={intl.formatMessage({
                id: 'invoice.nfse.domain.numberRps',
              })}
              name="numberRps"
              rules={[defaultEmptyFieldError]}
            >
              <Input disabled={disabledFields} />
            </FloatingFormItem>
          </Col>
        ) : null}

        {selecteInvoiceType === InvoiceType.NFE && (
          <Col span={24} md={12} lg={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'invoice.domain.emissionGoals',
              })}
              name="emissionGoal"
              rules={[defaultEmptyFieldError]}
              initialValue={InvoiceEmissionGoal.NFE_NORMAL}
            >
              <EmissionGoalSelect
                allowClear
                style={{ width: '100' }}
                disabled={disabledFields}
              />
            </FloatingFormItem>
          </Col>
        )}

        {isRelevantInvoiceType && (
          <Col span={24} md={12} lg={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'invoice.domain.emissionDate',
              })}
              name="emissionDate"
            >
              <InputDate
                disabled={
                  situation !== InvoiceSituation.IN_TYPING &&
                  situation !== InvoiceSituation.VALIDATED
                }
                showTime={isInternalApiIssued ? true : false}
              />
            </FloatingFormItem>
          </Col>
        )}

        {selecteInvoiceType != InvoiceType.NFS && (
          <>
            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'invoice.domain.documentType',
                })}
                name="documentType"
                rules={[defaultEmptyFieldError]}
                initialValue={InvoiceDocumentType.EXIT}
              >
                <Select
                  allowClear
                  style={{ width: '100' }}
                  options={[
                    {
                      value: InvoiceDocumentType.ENTRY,
                      label: intl.formatMessage({
                        id: 'invoice.domain.documentType.entry',
                      }),
                    },
                    {
                      value: InvoiceDocumentType.EXIT,
                      label: intl.formatMessage({
                        id: 'invoice.domain.documentType.exit',
                      }),
                    },
                  ]}
                  disabled={disabledFields}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'invoice.domain.finalCostumer',
                })}
                name="finalConsumer"
                rules={[defaultEmptyFieldError]}
              >
                <SelectYesNo allowClear disabled={disabledFields} />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'invoice.domain.operationDestination',
                })}
                name="operationDestination"
                rules={[defaultEmptyFieldError]}
              >
                <Select
                  allowClear
                  style={{ width: '100' }}
                  options={[
                    {
                      value: OperationDestination.internalOperation,
                      label: intl.formatMessage({
                        id: 'invoice.domain.operationDestination.internalOperation',
                      }),
                    },
                    {
                      value: OperationDestination.interstateOperation,
                      label: intl.formatMessage({
                        id: 'invoice.domain.operationDestination.interstateOperation',
                      }),
                    },
                    {
                      value: OperationDestination.foreignOperation,
                      label: intl.formatMessage({
                        id: 'invoice.domain.operationDestination.foreignOperation',
                      }),
                    },
                  ]}
                  disabled={disabledFields}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'invoice.domain.entryExitDate',
                })}
                name="departureDate"
              >
                <InputDate showTime disabled={disabledFields} />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'invoice.domain.emissionType',
                })}
                name="emissionType"
                rules={[defaultEmptyFieldError]}
                initialValue={InvoiceEmissionType.NORMAL_EMISSION}
              >
                <Select
                  allowClear
                  style={{ width: '100' }}
                  options={[
                    {
                      value: InvoiceEmissionType.NORMAL_EMISSION,
                      label: intl.formatMessage({
                        id: 'invoice.domain.emissionType.normal',
                      }),
                    },
                    {
                      value: InvoiceEmissionType.CONTINGENCY_FS_IA,
                      label: intl.formatMessage({
                        id: 'invoice.domain.emissionType.contingencyFs',
                      }),
                    },
                    {
                      value: InvoiceEmissionType.CONTINGENCY_DPEC,
                      label: intl.formatMessage({
                        id: 'invoice.domain.emissionType.contingencyDpec',
                      }),
                    },
                    {
                      value: InvoiceEmissionType.CONTINGENCY_FS_DA,
                      label: intl.formatMessage({
                        id: 'invoice.domain.emissionType.contingencyFsda',
                      }),
                    },
                    {
                      value: InvoiceEmissionType.CONTINGENCY_SVC_AN,
                      label: intl.formatMessage({
                        id: 'invoice.domain.emissionType.contingencySvcan',
                      }),
                    },
                    {
                      value: InvoiceEmissionType.CONTINGENCY_SVC_RS,
                      label: intl.formatMessage({
                        id: 'invoice.domain.emissionType.contingencySvcrs',
                      }),
                    },
                  ]}
                  disabled={disabledFields}
                />
              </FloatingFormItem>
            </Col>

            {(emissionType === InvoiceEmissionType.CONTINGENCY_SVC_AN ||
              emissionType === InvoiceEmissionType.CONTINGENCY_SVC_RS) && (
              <Col span={24} md={12} lg={6}>
                <FloatingFormItem
                  label="Justificativa de contingência"
                  name="contingencyJustification"
                  rules={[
                    defaultEmptyFieldError,
                    defaultMinLenghtFieldError(15),
                    defaultMaxLenghtFieldError(255),
                  ]}
                >
                  <Input />
                </FloatingFormItem>
              </Col>
            )}

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label="Operação presencial"
                name="faceToFace"
                rules={[defaultEmptyFieldError]}
              >
                <SelectYesNo allowClear disabled={disabledFields} />
              </FloatingFormItem>
            </Col>
          </>
        )}

        {selecteInvoiceType == InvoiceType.NFS ? (
          <>
            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'invoice.nfse.domain.simpleNational',
                })}
                name="simpleNational"
              >
                <Select
                  allowClear
                  style={{ width: '100' }}
                  options={[
                    {
                      value: 'true',
                      label: intl.formatMessage({
                        id: 'invoice.domain.true',
                      }),
                    },
                    {
                      value: 'false',
                      label: intl.formatMessage({
                        id: 'invoice.domain.false',
                      }),
                    },
                  ]}
                  disabled={disabledFields}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'invoice.nfse.domain.culturalIncentive',
                })}
                name="culturalIncentivator"
              >
                <Select
                  allowClear
                  style={{ width: '100' }}
                  options={[
                    {
                      value: 'true',
                      label: intl.formatMessage({
                        id: 'invoice.domain.true',
                      }),
                    },
                    {
                      value: 'false',
                      label: intl.formatMessage({
                        id: 'invoice.domain.false',
                      }),
                    },
                  ]}
                  disabled={disabledFields}
                />
              </FloatingFormItem>
            </Col>
          </>
        ) : null}

        {selecteInvoiceType === InvoiceType.NFS ? null : (
          <Col span={24} md={12} lg={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'invoice.domain.brokerIndicator',
              })}
              name="intermediaryIndication"
            >
              <Select
                allowClear
                style={{ width: '100' }}
                options={[
                  {
                    value: 'true',
                    label: intl.formatMessage({
                      id: 'invoice.domain.true',
                    }),
                  },
                  {
                    value: 'false',
                    label: intl.formatMessage({
                      id: 'invoice.domain.false',
                    }),
                  },
                ]}
                disabled={disabledFields}
              />
            </FloatingFormItem>
          </Col>
        )}

        <Col span={24} md={24} lg={12}>
          <InvoiceAddressFields
            form={form}
            cityId={invoiceData?.cityId}
            companyId={companyIdField}
            disabled={disabledFields}
          />
        </Col>

        {selecteInvoiceType === InvoiceType.NFS ? (
          <>
            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label="Código serviços"
                name="serviceItemList"
                rules={[defaultEmptyFieldError]}
              >
                <Input disabled={disabledFields} />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem label="Código de tributação" name="taxCode">
                <Input disabled={disabledFields} />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label="Código do contribuinte"
                name="taxpayerCode"
              >
                <Input maxLength={30} />
              </FloatingFormItem>
            </Col>
          </>
        ) : null}

        {situation && situation === 'AUTHORIZED' && isEdit && (
          <Col span={24} md={12} lg={6}>
            <FloatingFormItem label="Protocolo de autorização" name="protocol">
              <InputText onlyNumber disabled />
            </FloatingFormItem>
          </Col>
        )}

        {situation && situation === 'CANCELED' && isEdit && (
          <Col span={24} md={12} lg={6}>
            <FloatingFormItem
              label="Protocolo de cancelamento"
              name="cancellationProtocol"
            >
              <InputText onlyNumber disabled />
            </FloatingFormItem>
          </Col>
        )}

        <Col span={24} md={12} lg={6}>
          <Form.Item
            name="retentionPisCofins"
            initialValue={false}
            valuePropName="checked"
          >
            <Checkbox disabled={disabledFields} style={{ marginTop: '12px' }}>
              Reter PIS/COFINS
            </Checkbox>
          </Form.Item>
        </Col>

        {selecteInvoiceType === InvoiceType.NFS ? (
          <Col span={24}>
            <FloatingFormItem
              name="description"
              label={intl.formatMessage({
                id: 'invoice.nfse.domain.additionalInformation',
              })}
            >
              <Input.TextArea rows={4} disabled={disabledFields} />
            </FloatingFormItem>
          </Col>
        ) : null}

        {companyProvider?.provider === InvoiceProvider.SIAPPA &&
          invoice?.invoiceType === InvoiceType.NFS && (
            <Col md={24}>
              <FloatingFormItem
                name="rpsDescription"
                label={'Descrição do RPS'}
              >
                <Input.TextArea rows={4} disabled={disabledFields} />
              </FloatingFormItem>
            </Col>
          )}
      </Row>

      <Divider />

      <Row gutter={[16, 9]}>
        <Col span={24} md={12} lg={8}>
          <FloatingFormItem
            name="deliveryPrevisionDate"
            label="Previsão de entrega"
          >
            <InputDate disabled={disabledFields} />
          </FloatingFormItem>
        </Col>

        {/* TODO: Será implementado pelo back posteriormente */}
        {/* <Col span={24} md={12} lg={8}>
          <FloatingFormItem
            name="occurrenceCityIbsCbs"
            label="Cód. do Município de consumo"
          >
            <Input />
          </FloatingFormItem>
        </Col> */}

        <Col span={24} md={12} lg={8}>
          <FloatingFormItem name="governmentType" label="Tipo governamental">
            <SelectGovernmentType allowClear disabled={disabledFields} />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={8}>
          <FloatingFormItem
            name="governmentReducer"
            label="% de redução de alíq."
            extra="Relação a compra governamental"
          >
            <InputDecimal disabled={disabledFields} />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={8}>
          <FloatingFormItem
            name="governmentOperation"
            label="Tipo de operação governamental"
          >
            <SelectGovernmentOperation allowClear disabled={disabledFields} />
          </FloatingFormItem>
        </Col>

        {selecteInvoiceType === InvoiceType.NFS && (
          <>
            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name="operationIndicatorCode"
                label="Cód. Indicador de Operação"
              >
                <Input allowClear disabled={disabledFields} />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={8}>
              <FloatingFormItem name="operationType" label="Tipo de Operação">
                <SelectOperationType allowClear disabled={disabledFields} />
              </FloatingFormItem>
            </Col>
          </>
        )}
      </Row>
    </>
  );
};

export default InvoiceAllForm;
